import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowRight,
} from "lucide-react";

import {
  Link,
} from "react-router-dom";


const LOGIN_INACTIVE_DAYS = 7;


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function daysSinceTimestamp(value) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  const difference =
    Date.now() -
    date.getTime();

  return Math.max(
    0,
    Math.floor(
      difference /
        86400000
    )
  );
}


function formatTimestamp(value) {
  if (!value) {
    return "Never";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "Not available";
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function dayLabel(days) {
  return `${days} ${
    days === 1
      ? "day"
      : "days"
  }`;
}


function buildInactiveRows(
  progressRows
) {
  const rows =
    Array.isArray(
      progressRows
    )
      ? progressRows
      : [];


  return rows
    .filter(
      (learner) =>
        learner
          ?.membership_status ===
        "active"
    )
    .map(
      (learner) => {
        /*
         * Real login inactivity:
         *
         * This comes from auth.users.last_sign_in_at
         * exposed as last_login_at by
         * get_partner_cohort_progress().
         */
        const loginDays =
          daysSinceTimestamp(
            learner
              ?.last_login_at
          );


        if (
          loginDays !== null
        ) {
          if (
            loginDays <
            LOGIN_INACTIVE_DAYS
          ) {
            return null;
          }

          return {
            ...learner,

            login_inactivity_days:
              loginDays,

            login_inactivity_label:
              `${dayLabel(
                loginDays
              )} since login`,

            never_logged_in:
              false,
          };
        }


        /*
         * If there is no login timestamp,
         * only classify them as inactive
         * once they have been in the cohort
         * for at least 7 days.
         */
        const joinedDays =
          daysSinceTimestamp(
            learner?.joined_at
          );


        if (
          joinedDays === null ||
          joinedDays <
            LOGIN_INACTIVE_DAYS
        ) {
          return null;
        }


        return {
          ...learner,

          login_inactivity_days:
            joinedDays,

          login_inactivity_label:
            `Never logged in`,

          never_logged_in:
            true,
        };
      }
    )
    .filter(Boolean)
    .sort(
      (a, b) =>
        (
          b.login_inactivity_days ||
          0
        ) -
        (
          a.login_inactivity_days ||
          0
        )
    );
}


function visibleRows(
  rows,
  maxRows
) {
  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];

  if (
    !Number.isFinite(
      maxRows
    )
  ) {
    return safeRows;
  }

  return safeRows.slice(
    0,
    maxRows
  );
}


/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

export default function EntrepreneurStatusTabs({
  title = "Entrepreneurs",

  description =
    "Review entrepreneurs who may need follow-up.",

  attentionRows = [],

  invitationRows = [],

  progressRows = [],

  canViewPending = false,

  showCohort = true,

  maxRows = null,

  loading = false,

  progressUnavailable = false,

  pendingError = "",

  fullListRoutes = {},

  activeTab: controlledActiveTab,

  onActiveTabChange,
}) {
  const [
    internalActiveTab,
    setInternalActiveTab,
  ] = useState(
    "attention"
  );


  const activeTab =
    controlledActiveTab ||
    internalActiveTab;


  function changeTab(
    nextTab
  ) {
    if (
      onActiveTabChange
    ) {
      onActiveTabChange(
        nextTab
      );
    } else {
      setInternalActiveTab(
        nextTab
      );
    }
  }


  const pendingRows =
    useMemo(
      () =>
        (
          Array.isArray(
            invitationRows
          )
            ? invitationRows
            : []
        ).filter(
          (invitation) =>
            invitation?.status ===
            "pending"
        ),
      [invitationRows]
    );


  const inactiveRows =
    useMemo(
      () =>
        buildInactiveRows(
          progressRows
        ),
      [progressRows]
    );


  useEffect(() => {
  if (
    !canViewPending &&
    activeTab ===
      "pending"
  ) {
    if (
      onActiveTabChange
    ) {
      onActiveTabChange(
        "attention"
      );
    } else {
      setInternalActiveTab(
        "attention"
      );
    }
  }
}, [
  activeTab,
  canViewPending,
  onActiveTabChange,
]);


  const tabs = [
    {
      key:
        "attention",

      label:
        "Needs attention",

      count:
        attentionRows.length,
    },

    ...(canViewPending
      ? [
          {
            key:
              "pending",

            label:
              "Pending",

            count:
              pendingRows.length,
          },
        ]
      : []),

    {
      key:
        "inactive",

      label:
        "Inactive",

      count:
        inactiveRows.length,
    },
  ];


  const fullListRoute =
    fullListRoutes[
      activeTab
    ] || "";


  const activeDescription =
    activeTab ===
    "attention"
      ? "Entrepreneurs who may need additional support or follow-up."
      : activeTab ===
        "pending"
      ? "Entrepreneurs who have been invited but have not yet joined."
      : "Entrepreneurs who have not logged into Gwin for 7 days or more.";


  const progressTab =
    activeTab ===
      "attention" ||
    activeTab ===
      "inactive";


  return (
    <div className="overflow-hidden rounded-[14px] border border-[#D3D3D3] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">

      {/* ================================================================ */}
      {/* Header                                                           */}
      {/* ================================================================ */}

      <div className="px-5 py-5 sm:px-6">

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">

          <div>
            <h2 className="text-[20px] font-black tracking-[-0.025em] text-black sm:text-[22px]">
              {title}
            </h2>

            <p className="mt-1 text-sm font-medium text-[#858585]">
              {description}
            </p>
          </div>


          {fullListRoute && (
            <Link
              to={
                fullListRoute
              }
              className="inline-flex w-fit items-center gap-1.5 text-sm font-bold text-[#FF914D] transition hover:text-[#E9782F]"
            >
              View full list

              <ArrowRight className="h-4 w-4" />
            </Link>
          )}

        </div>


        {/* -------------------------------------------------------------- */}
        {/* Toggle tabs                                                    */}
        {/* -------------------------------------------------------------- */}

        <div className="mt-5 flex flex-wrap gap-2">

          {tabs.map(
            (tab) => {
              const active =
                activeTab ===
                tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() =>
                    changeTab(
                      tab.key
                    )
                  }
                  className={[
                    "inline-flex min-h-[38px] items-center gap-2 rounded-full border px-4 py-2 text-xs font-black transition",

                    active
                      ? "border-black bg-black text-white"
                      : "border-[#D8D8D8] bg-white text-[#555555] hover:border-[#FF914D] hover:bg-[#FFF9F5]",
                  ].join(" ")}
                >
                  <span>
                    {tab.label}
                  </span>

                  <span
                    className={[
                      "inline-flex min-w-[23px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-black",

                      active
                        ? "bg-[#FF914D] text-black"
                        : "bg-[#FFF1E7] text-[#D86620]",
                    ].join(
                      " "
                    )}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            }
          )}

        </div>


        <p className="mt-3 text-xs font-medium text-[#8A8A8A]">
          {activeDescription}
        </p>

      </div>


      {/* ================================================================ */}
      {/* Content                                                          */}
      {/* ================================================================ */}

      {loading ? (
        <LoadingState />
      ) : progressTab &&
        progressUnavailable ? (
        <UnavailableState />
      ) : activeTab ===
          "pending" &&
        pendingError ? (
        <ErrorState
          message={
            pendingError
          }
        />
      ) : activeTab ===
        "attention" ? (
        <AttentionTable
          rows={
            attentionRows
          }
          showCohort={
            showCohort
          }
          maxRows={
            maxRows
          }
        />
      ) : activeTab ===
        "pending" ? (
        <PendingTable
          rows={
            pendingRows
          }
          showCohort={
            showCohort
          }
          maxRows={
            maxRows
          }
        />
      ) : (
        <InactiveTable
          rows={
            inactiveRows
          }
          showCohort={
            showCohort
          }
          maxRows={
            maxRows
          }
        />
      )}

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Attention table                                                            */
/* -------------------------------------------------------------------------- */

function AttentionTable({
  rows,
  showCohort,
  maxRows,
}) {
  const displayRows =
    visibleRows(
      rows,
      maxRows
    );


  if (
    displayRows.length ===
    0
  ) {
    return (
      <EmptyState
        message="No entrepreneurs currently need attention."
      />
    );
  }


  const gridClass =
    showCohort
      ? "md:grid-cols-[1.15fr_1fr_1.35fr_1fr_76px]"
      : "md:grid-cols-[1.2fr_1.6fr_1fr_76px]";


  return (
    <>
      <div
        className={[
          "hidden gap-4 border-y border-[#D9D9D9] bg-[#F5F5F5] px-6 py-4 text-[11px] font-black uppercase tracking-wide text-[#777777] md:grid",
          gridClass,
        ].join(" ")}
      >
        <span>
          Entrepreneur
        </span>

        {showCohort && (
          <span>
            Cohort
          </span>
        )}

        <span>
          Attention reason
        </span>

        <span>
          Facilitator
        </span>

        <span />
      </div>


      <div className="divide-y divide-[#E4E4E4]">
        {displayRows.map(
          (row) => (
            <div
              key={
                row
                  .cohort_learner_id
              }
              className={[
                "grid gap-3 px-5 py-4 md:items-center md:gap-4 md:px-6",
                gridClass,
              ].join(" ")}
            >

              <div>
                <p className="font-bold text-black">
                  {row.full_name ||
                    row.email ||
                    "Entrepreneur"}
                </p>

                {row.email && (
                  <p className="mt-0.5 truncate text-xs text-[#888888] md:hidden">
                    {row.email}
                  </p>
                )}
              </div>


              {showCohort && (
                <MobileField
                  label="Cohort"
                  value={
                    row.cohort_name ||
                    "Cohort"
                  }
                />
              )}


              <div>
                <p className="text-[10px] font-black uppercase tracking-wide text-[#999999] md:hidden">
                  Attention reason
                </p>

                <p className="text-sm font-black text-black">
                  {
                    row.attention_label
                  }
                </p>

                <p className="mt-0.5 text-xs font-medium text-[#555555]">
                  {
                    row.attention_detail
                  }
                </p>
              </div>


              <MobileField
                label="Facilitator"
                value={
                  row.facilitator_name ||
                  "—"
                }
              />


              <Link
                to={`/partner/learners/${row.cohort_learner_id}`}
                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white transition hover:bg-[#2B2B2B]"
              >
                View
              </Link>

            </div>
          )
        )}
      </div>


      <PreviewFooter
        total={
          rows.length
        }
        displayed={
          displayRows.length
        }
        maxRows={
          maxRows
        }
        noun="entrepreneurs"
      />
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Pending table                                                              */
/* -------------------------------------------------------------------------- */

function PendingTable({
  rows,
  showCohort,
  maxRows,
}) {
  const displayRows =
    visibleRows(
      rows,
      maxRows
    );


  if (
    displayRows.length ===
    0
  ) {
    return (
      <EmptyState
        message="There are no pending entrepreneur invitations."
      />
    );
  }


  const gridClass =
    showCohort
      ? "md:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.7fr]"
      : "md:grid-cols-[1.5fr_0.9fr_0.9fr_0.7fr]";


  return (
    <>
      <div
        className={[
          "hidden gap-4 border-y border-[#D9D9D9] bg-[#F5F5F5] px-6 py-4 text-[11px] font-black uppercase tracking-wide text-[#777777] md:grid",
          gridClass,
        ].join(" ")}
      >
        <span>
          Email
        </span>

        {showCohort && (
          <span>
            Cohort
          </span>
        )}

        <span>
          Invited
        </span>

        <span>
          Expires
        </span>

        <span>
          Status
        </span>
      </div>


      <div className="divide-y divide-[#E4E4E4]">
        {displayRows.map(
          (invitation) => (
            <div
              key={
                invitation
                  .invitation_id
              }
              className={[
                "grid gap-3 px-5 py-4 md:items-center md:gap-4 md:px-6",
                gridClass,
              ].join(" ")}
            >

              <div>
                <p className="break-all text-sm font-bold text-black">
                  {
                    invitation.email
                  }
                </p>
              </div>


              {showCohort && (
                <MobileField
                  label="Cohort"
                  value={
                    invitation
                      .cohort_name ||
                    "Cohort"
                  }
                />
              )}


              <MobileField
                label="Invited"
                value={formatTimestamp(
                  invitation
                    .invited_at
                )}
              />


              <MobileField
                label="Expires"
                value={formatTimestamp(
                  invitation
                    .expires_at
                )}
              />


              <div>
                <p className="text-[10px] font-black uppercase tracking-wide text-[#999999] md:hidden">
                  Status
                </p>

                <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-black text-amber-800">
                  Pending
                </span>
              </div>

            </div>
          )
        )}
      </div>


      <PreviewFooter
        total={
          rows.length
        }
        displayed={
          displayRows.length
        }
        maxRows={
          maxRows
        }
        noun="invitations"
      />
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Inactive table                                                             */
/* -------------------------------------------------------------------------- */

function InactiveTable({
  rows,
  showCohort,
  maxRows,
}) {
  const displayRows =
    visibleRows(
      rows,
      maxRows
    );


  if (
    displayRows.length ===
    0
  ) {
    return (
      <EmptyState
        message="No entrepreneurs have been inactive for 7 days or more."
      />
    );
  }


  const gridClass =
    showCohort
      ? "md:grid-cols-[1.3fr_1fr_1fr_0.9fr_76px]"
      : "md:grid-cols-[1.4fr_1fr_1fr_76px]";


  return (
    <>
      <div
        className={[
          "hidden gap-4 border-y border-[#D9D9D9] bg-[#F5F5F5] px-6 py-4 text-[11px] font-black uppercase tracking-wide text-[#777777] md:grid",
          gridClass,
        ].join(" ")}
      >
        <span>
          Entrepreneur
        </span>

        {showCohort && (
          <span>
            Cohort
          </span>
        )}

        <span>
          Last login
        </span>

        <span>
          Inactive for
        </span>

        <span />
      </div>


      <div className="divide-y divide-[#E4E4E4]">
        {displayRows.map(
          (row) => (
            <div
              key={
                row
                  .cohort_learner_id
              }
              className={[
                "grid gap-3 px-5 py-4 md:items-center md:gap-4 md:px-6",
                gridClass,
              ].join(" ")}
            >

              <div>
                <p className="font-bold text-black">
                  {row.full_name ||
                    row.email ||
                    "Entrepreneur"}
                </p>

                {row.email && (
                  <p className="mt-0.5 text-xs text-[#888888]">
                    {row.email}
                  </p>
                )}
              </div>


              {showCohort && (
                <MobileField
                  label="Cohort"
                  value={
                    row.cohort_name ||
                    "Cohort"
                  }
                />
              )}


              <div>
                <p className="text-[10px] font-black uppercase tracking-wide text-[#999999] md:hidden">
                  Last login
                </p>

                <p className="text-sm font-medium text-[#333333]">
                  {row
                    .never_logged_in
                    ? "Never logged in"
                    : formatTimestamp(
                        row.last_login_at
                      )}
                </p>
              </div>


              <div>
                <p className="text-[10px] font-black uppercase tracking-wide text-[#999999] md:hidden">
                  Inactive for
                </p>

                <p className="text-sm font-black text-black">
                  {dayLabel(
                    row
                      .login_inactivity_days
                  )}
                </p>

                {row
                  .never_logged_in && (
                  <p className="mt-0.5 text-xs font-medium text-[#777777]">
                    Since joining
                  </p>
                )}
              </div>


              <Link
                to={`/partner/learners/${row.cohort_learner_id}`}
                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white transition hover:bg-[#2B2B2B]"
              >
                View
              </Link>

            </div>
          )
        )}
      </div>


      <PreviewFooter
        total={
          rows.length
        }
        displayed={
          displayRows.length
        }
        maxRows={
          maxRows
        }
        noun="entrepreneurs"
      />
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Small UI helpers                                                           */
/* -------------------------------------------------------------------------- */

function MobileField({
  label,
  value,
}) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-wide text-[#999999] md:hidden">
        {label}
      </p>

      <p className="text-sm font-medium text-[#333333]">
        {value}
      </p>
    </div>
  );
}


function PreviewFooter({
  total,
  displayed,
  maxRows,
  noun,
}) {
  if (
    !Number.isFinite(
      maxRows
    )
  ) {
    return null;
  }

  return (
    <div className="border-t border-[#D9D9D9] px-5 py-3.5 sm:px-6">
      <p className="text-xs font-medium text-[#858585]">
        Showing {displayed} of{" "}
        {total} {noun}.
      </p>
    </div>
  );
}


function LoadingState() {
  return (
    <div className="border-t border-[#D9D9D9] px-6 py-12 text-center">
      <p className="font-semibold text-[#777777]">
        Loading entrepreneurs...
      </p>
    </div>
  );
}


function EmptyState({
  message,
}) {
  return (
    <div className="border-t border-[#D9D9D9] px-6 py-12 text-center">
      <p className="font-bold text-[#555555]">
        {message}
      </p>
    </div>
  );
}


function UnavailableState() {
  return (
    <div className="border-t border-amber-200 bg-amber-50 px-6 py-10 text-center">
      <p className="font-black text-amber-900">
        Entrepreneur status unavailable
      </p>

      <p className="mt-1 text-sm font-medium text-amber-800">
        The entrepreneur progress source could not be loaded safely.
      </p>
    </div>
  );
}


function ErrorState({
  message,
}) {
  return (
    <div className="border-t border-amber-200 bg-amber-50 px-6 py-10 text-center">
      <p className="font-black text-amber-900">
        Pending invitations unavailable
      </p>

      <p className="mt-1 text-sm font-medium text-amber-800">
        {message}
      </p>
    </div>
  );
}